import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation, Link as RouterLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    CircularProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Paper,
    Avatar,
    IconButton,
    Breadcrumbs,
    Link,
    Grid,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    YouTube as YouTubeIcon,
    PlayArrow as PlayArrowIcon,
    ChevronRight as ChevronRightIcon,
    SwipeLeft as SwipeLeftIcon,
} from '@mui/icons-material';
import apiService from '../services/api';
import { getS3Url, ensureDirectS3Url } from '../utils/imageUtils';
import { ROUTES_NAMES } from '../components/Layout';
import { useBrandExplorerStore } from '../stores/brandExplorerStore';
import { useTrackerStore } from '../stores/trackerStore';
import { useAuth } from '../contexts/AuthContext';
import { productBrand } from '../productBrand';
import { TrendChartCard } from './Explorer/TrendChartCard';

// Helper function to calculate median
const calculateMedian = (values: number[]): number => {
    if (values.length === 0) return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

interface BrandExplorerProgramDetailProps {
    fixedBrandId?: string;
}

const BrandExplorerProgramDetail: React.FC<BrandExplorerProgramDetailProps> = ({ fixedBrandId }) => {
    const { programId } = useParams<{ programId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { selectedBrand } = useBrandExplorerStore();
    const { startDate: trackerStartDate, endDate: trackerEndDate } = useTrackerStore();
    const { getCompanyTrackerBrandId } = useAuth();

    const isTracker = location.pathname.startsWith('/tracker');

    // Use English for tonybet route (fixedBrandId), Spanish otherwise
    const isEnglish = !!fixedBrandId;

    // Tracker: brand and date range from store. Brand Explorer: from URL or store.
    const brandIdParam = searchParams.get('brandId');
    const yearParam = searchParams.get('year');
    const platform = searchParams.get('platform') as 'yt' | 'ig' | null;
    const startDateParam = isTracker ? trackerStartDate : (searchParams.get('startDate') || undefined);
    const endDateParam = isTracker ? trackerEndDate : (searchParams.get('endDate') || undefined);
    const yearFromUrl = yearParam ? parseInt(yearParam) : undefined;

    const brandId = fixedBrandId || (isTracker ? getCompanyTrackerBrandId() : null) || selectedBrand?._id || brandIdParam || null;
    // For TonyBet (fixedBrandId), always use 2026 regardless of persisted store value
    const year = fixedBrandId ? 2026 : (yearFromUrl);

    // Sorting state - default to date descending (newest first)
    const [orderBy, setOrderBy] = useState<'publishedAt' | 'time' | 'views' | 'title'>('publishedAt');
    const [order, setOrder] = useState<'asc' | 'desc'>('desc');

    // Fetch program details
    const { data, isLoading, error } = useQuery({
        queryKey: ['brand-program-detail', brandId, programId, year, startDateParam, endDateParam],
        queryFn: () => apiService.getBrandProgramDetail(brandId!, programId!, year, startDateParam, endDateParam),
        enabled: !!brandId && !!programId,
        staleTime: 10 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
    });

    // Calculate the latest metrics collection date
    const latestMetricsDate = useMemo((): Date | null => {
        if (!data?.mentions) return null;

        let latestDate: Date | null = null;
        data.mentions.forEach(mention => {
            const collectedAt = (mention.content as any)?.metricsCollectedAt;
            if (collectedAt) {
                const date = new Date(collectedAt);
                if (!latestDate || date > latestDate) {
                    latestDate = date;
                }
            }
        });

        return latestDate;
    }, [data?.mentions]);

    // Handle sort request
    const handleRequestSort = (property: typeof orderBy) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    // Helper function to get ISO week number
    const getWeekNumber = (date: Date): number => {
        const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        const dayNum = d.getUTCDay() || 7;
        d.setUTCDate(d.getUTCDate() + 4 - dayNum);
        const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
        return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
    };

    // Helper function to get week start date
    const getWeekStart = (date: Date): Date => {
        const d = new Date(date);
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Monday as first day
        return new Date(d.setDate(diff));
    };

    // Calculate week-by-week trends
    const weeklyTrends = useMemo(() => {
        if (!data?.mentions) return [];

        // Group by week
        const weekMap = new Map<string, {
            week: string;
            weekStart: Date;
            episodes: Set<string>;
            mentions: number;
            views: number;
        }>();

        data.mentions.forEach(mention => {
            const date = new Date(mention.content.publishedAt);
            const weekStart = getWeekStart(date);
            const weekKey = `${weekStart.getFullYear()}-W${String(getWeekNumber(date)).padStart(2, '0')}`;
            const weekLabel = `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;

            if (!weekMap.has(weekKey)) {
                weekMap.set(weekKey, {
                    week: weekLabel,
                    weekStart: weekStart,
                    episodes: new Set(),
                    mentions: 0,
                    views: 0,
                });
            }

            const weekData = weekMap.get(weekKey)!;

            if (mention.content?._id) {
                weekData.episodes.add(mention.content._id);
            }
            weekData.mentions += 1;
            weekData.views += mention.content.views || 0;
        });

        // Convert to array and sort by week
        return Array.from(weekMap.values())
            .sort((a, b) => a.weekStart.getTime() - b.weekStart.getTime())
            .map(data => ({
                week: data.week,
                weekStart: data.weekStart.toISOString(),
                episodes: data.episodes.size,
                mentions: data.mentions,
                views: data.views,
            }));
    }, [data?.mentions]);

    // Sort mentions
    const sortedMentions = useMemo(() => {
        if (!data?.mentions) return [];
        return [...data.mentions].sort((a, b) => {
            let aValue: any;
            let bValue: any;

            switch (orderBy) {
                case 'publishedAt':
                    aValue = new Date(a.content.publishedAt).getTime();
                    bValue = new Date(b.content.publishedAt).getTime();
                    break;
                case 'time':
                    aValue = a.segmentStart;
                    bValue = b.segmentStart;
                    break;
                case 'views':
                    aValue = a.content.views || 0;
                    bValue = b.content.views || 0;
                    break;
                case 'title':
                    aValue = a.content.title.toLowerCase();
                    bValue = b.content.title.toLowerCase();
                    break;
                default:
                    return 0;
            }

            if (bValue < aValue) {
                return order === 'desc' ? -1 : 1;
            }
            if (bValue > aValue) {
                return order === 'desc' ? 1 : -1;
            }
            return 0;
        });
    }, [data?.mentions, orderBy, order]);

    // Generate YouTube URL with timestamp
    const generateYouTubeUrl = (url: string | undefined, startTime: number): string | null => {
        if (!url) return null;
        if (url.includes('youtube.com') || url.includes('youtu.be')) {
            const urlObj = new URL(url);
            urlObj.searchParams.set('t', Math.floor(startTime).toString() + 's');
            return urlObj.toString();
        }
        return null;
    };

    // Format duration
    const formatDuration = (seconds: number): string => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Format number with commas
    const formatNumber = (num: number): string => {
        return new Intl.NumberFormat('es-ES').format(num);
    };

    const handleBack = () => {
        const baseRoute = fixedBrandId
            ? ROUTES_NAMES.TONYBET_EXPLORER_YEAR
            : isTracker
                ? ROUTES_NAMES.TRACKER
                : ROUTES_NAMES.TONYBET_EXPLORER_YEAR;
        if (fixedBrandId || isTracker) {
            navigate(baseRoute);
        } else {
            const params = new URLSearchParams();
            if (brandId) params.set('brandId', brandId);
            if (year !== undefined) params.set('year', year.toString());
            if (platform) params.set('platform', platform);
            const queryString = params.toString();
            navigate(queryString ? `${baseRoute}?${queryString}` : baseRoute);
        }
    };

    if (isLoading) {
        return (
            <Container maxWidth="xl" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    if (error || !data) {
        return (
            <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
                <Alert severity="error">
                    Error al cargar los detalles del programa. Por favor, intenta de nuevo.
                </Alert>
            </Container>
        );
    }

    const { brand, program, podcast, statistics } = data;

    return (
        <>
            {/* Podscope Header - Only show for public route */}
            {fixedBrandId && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '64px',
                        display: 'flex',
                        alignItems: 'center',
                        px: { xs: 2, sm: 3 },
                        backgroundColor: '#ffffff',
                        zIndex: 1100,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <Container maxWidth="xl" disableGutters sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
                        <Typography
                            component="h1"
                            variant="h1"
                            onClick={() => window.open(productBrand.marketingUrl, '_blank', 'noopener,noreferrer')}
                            sx={{
                                fontSize: '1.5rem',
                                fontWeight: 800,
                                color: 'primary.main',
                                cursor: 'pointer',
                                '&:hover': {
                                    opacity: 0.8,
                                },
                            }}
                        >
                            {productBrand.name}
                        </Typography>
                    </Container>
                </Box>
            )}
            <Container maxWidth="xl" disableGutters sx={{
                mt: fixedBrandId ? { xs: 10, sm: 11, md: 12 } : 4,
                mb: 4,
                px: { xs: 2, sm: 3, md: 4 },
            }}>
                {/* Breadcrumb Navigation - Only show if not using fixed brand or tracker */}
                {!fixedBrandId && !isTracker && (
                    <Breadcrumbs
                        aria-label="breadcrumb"
                        separator={<ChevronRightIcon fontSize="small" />}
                        sx={{ mb: 2 }}
                    >
                        <Link
                            component={RouterLink}
                            to={isTracker ? ROUTES_NAMES.TRACKER : ROUTES_NAMES.TONYBET_EXPLORER_YEAR}
                            underline="hover"
                            color="inherit"
                        >
                            {isTracker ? 'Tracker' : 'TonyBet'}
                        </Link>
                        {brand && (
                            <Link
                                component={RouterLink}
                                to={isTracker ? ROUTES_NAMES.TRACKER : `${ROUTES_NAMES.TONYBET_EXPLORER_YEAR}?brandId=${brandId}`}
                                underline="hover"
                                color="inherit"
                            >
                                {brand.name}
                            </Link>
                        )}
                        {year !== undefined && (
                            isTracker
                                ? <Typography color="text.primary">{year}</Typography>
                                : (
                                    <Link
                                        component={RouterLink}
                                        to={`${isTracker ? ROUTES_NAMES.TRACKER : ROUTES_NAMES.TONYBET_EXPLORER_YEAR}?brandId=${brandId}&year=${year}`}
                                        underline="hover"
                                        color="inherit"
                                    >
                                        {year}
                                    </Link>
                                )
                        )}
                        {program && (
                            <Typography color="text.primary">
                                {program.name}
                            </Typography>
                        )}
                    </Breadcrumbs>
                )}

                {/* Header */}
                <Box sx={{ mb: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <IconButton onClick={handleBack} sx={{ mr: -1 }}>
                            <ArrowBackIcon />
                        </IconButton>
                        {program.imageS3Key ? (
                            <Avatar
                                src={ensureDirectS3Url(getS3Url(program.imageS3Key))}
                                sx={{ width: 56, height: 56 }}
                            />
                        ) : (
                            <Avatar sx={{ width: 56, height: 56 }}>
                                <PlayArrowIcon />
                            </Avatar>
                        )}
                        <Box>
                            <Typography variant="h1" component="h1" sx={{ fontSize: '2rem', fontWeight: 600, mb: 0.1 }}>
                                {program.name}
                            </Typography>
                            {podcast && (
                                <Typography variant="body1" color="text.secondary">
                                    {podcast.title}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Box>

                {/* Metrics date label */}
                {latestMetricsDate && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {isEnglish
                            ? `Views data as of ${latestMetricsDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                            : `Datos de vistas al ${latestMetricsDate.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}`
                        }
                    </Typography>
                )}

                {/* Statistics */}
                {statistics && (
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
                            {isEnglish ? 'Summary' : 'Resumen'}
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 6, sm: 3 }}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h4" color="primary">
                                            {formatNumber(statistics.uniqueEpisodes)}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {isEnglish ? 'Episodes' : 'Episodios'}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid size={{ xs: 6, sm: 3 }}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h4" color="primary">
                                            {formatNumber(statistics.totalMentions)}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {isEnglish ? 'Mentions' : 'Menciones'}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid size={{ xs: 6, sm: 3 }}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h4" color="primary">
                                            {formatNumber(statistics.accumulatedViews)}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Accumulated Views
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Box>
                )}

                {/* Weekly Trends Section - 4 Independent Charts */}
                {weeklyTrends.length > 0 && (
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                            {isEnglish ? 'Weekly Trends' : 'Tendencias Semanales'}
                        </Typography>

                        {/* Charts: Views, Mentions, Episodes */}
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 6, lg: 4 }}>
                                <TrendChartCard
                                    title="Views"
                                    dataKey="views"
                                    color="#1976d2"
                                    tooltipLabel="Views"
                                    granularityTooltip={isEnglish ? 'Week' : 'Semana'}
                                    data={weeklyTrends}
                                    median={calculateMedian(weeklyTrends.map(t => t.views))}
                                    formatValue={formatNumber}
                                    yAxisWidth={45}
                                />
                            </Grid>
                            <Grid size={{ xs: 6, lg: 4 }}>
                                <TrendChartCard
                                    title={isEnglish ? 'Mentions' : 'Menciones'}
                                    dataKey="mentions"
                                    color="#ed6c02"
                                    tooltipLabel={isEnglish ? 'Mentions' : 'Menciones'}
                                    granularityTooltip={isEnglish ? 'Week' : 'Semana'}
                                    data={weeklyTrends}
                                    median={calculateMedian(weeklyTrends.map(t => t.mentions))}
                                    allowDecimals={false}
                                />
                            </Grid>
                            <Grid size={{ xs: 6, lg: 4 }}>
                                <TrendChartCard
                                    title={isEnglish ? 'Episodes' : 'Episodios'}
                                    dataKey="episodes"
                                    color="#9c27b0"
                                    tooltipLabel={isEnglish ? 'Episodes' : 'Episodios'}
                                    granularityTooltip={isEnglish ? 'Week' : 'Semana'}
                                    data={weeklyTrends}
                                    median={calculateMedian(weeklyTrends.map(t => t.episodes))}
                                    allowDecimals={false}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                )}

                {/* Mentions Table */}
                <Box sx={{ mt: 4 }}>
                    <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
                        {isEnglish ? 'Brand Mentions' : 'Menciones de Marca'}
                    </Typography>
                    {sortedMentions.length > 0 ? (
                        <TableContainer component={Paper} variant="outlined">
                            <Box
                                sx={{
                                    display: { xs: 'flex', md: 'none' },
                                    alignItems: 'center',
                                    gap: 1,
                                    px: 2,
                                    py: 1,
                                    color: 'text.secondary',
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                }}
                            >
                                <SwipeLeftIcon />
                                <Typography variant="caption">Desliza para ver más</Typography>
                            </Box>
                            <Table sx={{ tableLayout: 'fixed' }}>
                                <TableHead>
                                    <TableRow>
                                        {/* Content - left aligned */}
                                        <TableCell align="left" sx={{ width: '30%', padding: { xs: '12px', sm: '16px' } }}>
                                            <TableSortLabel
                                                active={orderBy === 'title'}
                                                direction={orderBy === 'title' ? order : 'asc'}
                                                onClick={() => handleRequestSort('title')}
                                            >
                                                Content
                                            </TableSortLabel>
                                        </TableCell>
                                        {/* Published */}
                                        <TableCell align="right" sx={{ padding: { xs: '12px', sm: '16px' } }}>
                                            <TableSortLabel
                                                active={orderBy === 'publishedAt'}
                                                direction={orderBy === 'publishedAt' ? order : 'desc'}
                                                onClick={() => handleRequestSort('publishedAt')}
                                            >
                                                Published
                                            </TableSortLabel>
                                        </TableCell>
                                        {/* Time - hidden on mobile */}
                                        <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' }, padding: { xs: '12px', sm: '16px' } }}>
                                            <TableSortLabel
                                                active={orderBy === 'time'}
                                                direction={orderBy === 'time' ? order : 'asc'}
                                                onClick={() => handleRequestSort('time')}
                                            >
                                                Time
                                            </TableSortLabel>
                                        </TableCell>
                                        {/* Views - right aligned */}
                                        <TableCell align="right" sx={{ padding: { xs: '12px', sm: '16px' } }}>
                                            <TableSortLabel
                                                active={orderBy === 'views'}
                                                direction={orderBy === 'views' ? order : 'asc'}
                                                onClick={() => handleRequestSort('views')}
                                            >
                                                Views
                                            </TableSortLabel>
                                        </TableCell>
                                        {/* Link - right aligned */}
                                        <TableCell align="right" sx={{ padding: { xs: '12px', sm: '16px' } }}>
                                            Link
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sortedMentions.map((mention) => {
                                        const youtubeUrl = generateYouTubeUrl(mention.content.url, mention.segmentStart);
                                        return (
                                            <TableRow key={mention._id} hover>
                                                <TableCell sx={{ width: '30%', overflow: 'hidden', padding: { xs: '12px', sm: '16px' } }} align="left">
                                                    <Box sx={{ width: '100%', overflow: 'hidden' }}>
                                                        <Typography
                                                            variant="body2"
                                                            fontWeight="medium"
                                                            sx={{
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                                width: '100%',
                                                                display: 'block'
                                                            }}
                                                        >
                                                            {mention.content.title}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            sx={{
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                                width: '100%',
                                                                display: 'block'
                                                            }}
                                                        >
                                                            {mention.context}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="right" sx={{ padding: { xs: '12px', sm: '16px' } }}>
                                                    <Typography variant="caption">
                                                        {new Date(mention.content.publishedAt).toLocaleDateString()}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' }, padding: { xs: '12px', sm: '16px' } }}>
                                                    <Typography variant="body2">
                                                        {formatDuration(mention.segmentStart)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right" sx={{ padding: { xs: '12px', sm: '16px' } }}>
                                                    <Typography variant="body2">
                                                        {formatNumber(mention.content.views || 0)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right" sx={{ padding: { xs: '12px', sm: '16px' } }}>
                                                    {youtubeUrl ? (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => window.open(youtubeUrl!, '_blank')}
                                                            sx={{ color: '#FF0000' }}
                                                            title="Ver en YouTube"
                                                        >
                                                            <YouTubeIcon />
                                                        </IconButton>
                                                    ) : mention.content.url ? (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => window.open(mention.content.url!, '_blank')}
                                                            title="Abrir contenido"
                                                        >
                                                            <PlayArrowIcon />
                                                        </IconButton>
                                                    ) : (
                                                        <Typography variant="caption" color="text.disabled">
                                                            Sin enlace
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Alert severity="info">
                            No se encontraron menciones para este programa.
                        </Alert>
                    )}
                </Box>
            </Container>
        </>
    );
};

export default BrandExplorerProgramDetail;

